import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Typography, useTheme, Paper, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert, Checkbox, ListItemText, OutlinedInput, List, ListItem, ListItemButton, Chip, IconButton, useMediaQuery, Grid, Drawer, Slider, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../theme";
import { uploadFitFile, downloadUserFile, deleteUserFile } from "../api/client";
import { clearParametersCache, getUserParameters } from "../services/userParametersService";
import { useState, useMemo, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useWorkoutSession } from "../contexts/WorkoutSessionContext";
import StatBox from "../components/StatBox";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import SpeedIcon from "@mui/icons-material/Speed";
import BoltIcon from "@mui/icons-material/Bolt";
import TerrainIcon from "@mui/icons-material/Terrain";
import TimerIcon from "@mui/icons-material/Timer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RouteIcon from '@mui/icons-material/Route';
import PauseIcon from '@mui/icons-material/Pause';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ShareIcon from "@mui/icons-material/Share";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";
import ShareBikeAnalysisDialog from "../components/ShareBikeAnalysisDialog";
import { getSharedBikeAnalysis, unshareBikeAnalysis } from "../api/bikeShareApi";
import MoreActionsPanel from "../components/MoreActionsPanel";
import WhatIfSummaryBox from "../components/WhatIfSummaryBox";
import GradientAnalysisSection from "../components/GradientAnalysisSection";
import SpeedAnalysisSection from "../components/SpeedAnalysisSection";
import WorkoutChartsSection from "../components/WorkoutChartsSection";
import StaminaChart from "../components/StaminaChart";
import GpxPickerDrawer from "../components/GpxPickerDrawer";
import BikeRouteMap from "../components/BikeRouteMap";
import { BikeClimbsGrid, BikeDescentGrid } from "../components/BikeClimbsGrid";
import BikeClimbsChart from "../components/BikeClimbsChart";
import BikeComparisonSelectorDrawer from "../components/BikeComparisonSelectorDrawer";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import BikePlanGrid from "../components/bike/BikePlanGrid";
import BikePlanPrintContent from "../components/bike/BikePlanPrintContent";
import { buildBikePlan, downloadBikePlanFit, downloadBikePlanZwo } from "../api/client";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import { useReactToPrint } from "react-to-print";

// Utility functions for unit conversion
export const convertDistance = (meters, units) => units === 'imperial' ? meters * 0.000621371 : meters / 1000;
export const convertSpeed = (kmh, units) => units === 'imperial' ? kmh * 0.621371 : kmh;
export const convertElevation = (meters, units) => units === 'imperial' ? meters * 3.28084 : meters;
export const convertWeight = (kg, units) => units === 'imperial' ? kg * 2.20462 : kg;

export const getUnits = () => localStorage.getItem('units') || 'metric';
export const distanceLabel = getUnits() === 'imperial' ? 'mi' : 'km';
export const speedLabel = getUnits() === 'imperial' ? 'mph' : 'km/h';
export const elevationLabel = getUnits() === 'imperial' ? 'ft' : 'm';
export const weightLabel = getUnits() === 'imperial' ? 'lb' : 'kg';

// Dynamic chart labels based on units
export const getChartLabels = (units, t) => {
  return {
    distanceLabel: units === 'imperial' ? `${t('distance', 'Distance')} (mi)` : `${t('distance', 'Distance')} (km)`,
    speedLabel: units === 'imperial' ? `${t('avgSpeed', 'Avg Speed')} (mph)` : `${t('avgSpeed', 'Avg Speed')} (km/h)`,
    elevationLabel: units === 'imperial' ? `${t('altitude', 'Altitude')} (ft)` : `${t('altitude', 'Altitude')} (m)`,
    gradientLabel: `${t('gradient', 'Gradient')} (%)`,
    powerLabel: `${t('power', 'Power')} (W)`,
    assumedCdaLabel: t('assumedCda', 'Assumed CdA'),
    calculatedCdaLabel: t('calculatedCda', 'Calculated CdA'),
    positionLabel: t('bikePositionMetric', 'Bike Position')
  };
};

// Example usage in rendering:
// <Typography>Total Distance: {convertDistance(total_distance, getUnits()).toFixed(2)} {distanceLabel}</Typography>
// <Typography>Avg Speed: {convertSpeed(avg_speed, getUnits()).toFixed(2)} {speedLabel}</Typography>
// <Typography>Total Elevation Gain: {convertElevation(total_elevation_gain, getUnits()).toFixed(0)} {elevationLabel}</Typography>
// <Typography>Rider Weight: {convertWeight(riderWeight, getUnits()).toFixed(1)} {weightLabel}</Typography>

const WorkoutAnalysisV2 = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();
  const { shareId } = useParams();
  const { user } = useAuth();
  const { setActiveSessionData } = useWorkoutSession();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const initialWorkoutData = location.state?.workoutData;
  const storedFile = location.state?.file;
  const initialReusedFileId = location.state?.reusedFileId;

  const [workoutData, setWorkoutData] = useState(initialWorkoutData);
  const [reusedFileId, setReusedFileId] = useState(initialReusedFileId);

  // Keep topbar session context in sync with current workout
  useEffect(() => {
    setActiveSessionData(workoutData || null);
    return () => setActiveSessionData(null);
  }, [workoutData, setActiveSessionData]);

  // Share mode state
  const [shareInfo, setShareInfo] = useState(null);
  const [shareLoading, setShareLoading] = useState(!!shareId);
  const [shareError, setShareError] = useState('');
  const [unshareDialogOpen, setUnshareDialogOpen] = useState(false);
  const [unshareLoading, setUnshareLoading] = useState(false);
  const [analysisType, setAnalysisType] = useState(0);
  const [activeClimbRange, setActiveClimbRange] = useState(null);
  const [activeDescentRange, setActiveDescentRange] = useState(null);
  const [bikePlanSegments, setBikePlanSegments] = useState(null);
  const [bikePlanLoading, setBikePlanLoading] = useState(false);
  const [bikePlanError, setBikePlanError] = useState('');
  const [planSelectedSegment, setPlanSelectedSegment] = useState(null);
  const [planChartZoom, setPlanChartZoom] = useState(null);
  const bikePlanPrintRef = useRef();
  const handlePrintBikePlan = useReactToPrint({ contentRef: bikePlanPrintRef });
  const [isLoading, setIsLoading] = useState(false);
  
  // Editable parameters state
  const [bikeType, setBikeType] = useState(initialWorkoutData?.parameters?.bike_type || 'road');
  const [riderWeight, setRiderWeight] = useState(initialWorkoutData?.parameters?.rider_weight || 75);
  const defaultBikeWeight = initialWorkoutData?.parameters?.bike_weight || ((initialWorkoutData?.parameters?.bike_type || 'road') === 'tt' ? 10 : 8.5);
  const [bikeWeight, setBikeWeight] = useState(defaultBikeWeight);
  const [ttAeroMinSpeed, setTtAeroMinSpeed] = useState(initialWorkoutData?.parameters?.tt_aero_min_speed || 15);
  const [ttAeroMaxSpeed, setTtAeroMaxSpeed] = useState(initialWorkoutData?.parameters?.tt_aero_max_speed || 50);
  const [roadDropsMinSpeed, setRoadDropsMinSpeed] = useState(initialWorkoutData?.parameters?.road_drops_min_speed || 35);
  const [roadAeroAerobarMinSpeed, setRoadAeroAerobarMinSpeed] = useState(initialWorkoutData?.parameters?.road_aero_aerobar_min_speed || 18);
  const [roadAeroAerobarMaxSpeed, setRoadAeroAerobarMaxSpeed] = useState(initialWorkoutData?.parameters?.road_aero_aerobar_max_speed || 40);
  const [roadAeroDropsMinSpeed, setRoadAeroDropsMinSpeed] = useState(initialWorkoutData?.parameters?.road_aero_drops_min_speed || 40);
  const [gravelDropsMinSpeed, setGravelDropsMinSpeed] = useState(initialWorkoutData?.parameters?.gravel_drops_min_speed || 30);
  const [terrainType, setTerrainType] = useState(initialWorkoutData?.parameters?.terrain_type || 'paved');
  const [terrainPavedPercentage, setTerrainPavedPercentage] = useState(initialWorkoutData?.parameters?.terrain_paved_percentage || 100);

  const [whatIfBikeType, setWhatIfBikeType] = useState(initialWorkoutData?.parameters?.what_if_bike_type || initialWorkoutData?.parameters?.bike_type || 'road');
  const [whatIfRiderWeight, setWhatIfRiderWeight] = useState(initialWorkoutData?.parameters?.what_if_rider_weight || initialWorkoutData?.parameters?.rider_weight || 75);
  const defaultWhatIfBikeWeight = initialWorkoutData?.parameters?.what_if_bike_weight || initialWorkoutData?.parameters?.bike_weight || ((initialWorkoutData?.parameters?.what_if_bike_type || initialWorkoutData?.parameters?.bike_type || 'road') === 'tt' ? 10 : 8.5);
  const [whatIfBikeWeight, setWhatIfBikeWeight] = useState(defaultWhatIfBikeWeight);
  const [whatIfTtAeroMinSpeed, setWhatIfTtAeroMinSpeed] = useState(initialWorkoutData?.parameters?.what_if_tt_aero_min_speed || 15);
  const [whatIfTtAeroMaxSpeed, setWhatIfTtAeroMaxSpeed] = useState(initialWorkoutData?.parameters?.what_if_tt_aero_max_speed || 50);
  const [whatIfRoadDropsMinSpeed, setWhatIfRoadDropsMinSpeed] = useState(initialWorkoutData?.parameters?.what_if_road_drops_min_speed || 35);
  const [whatIfRoadAeroAerobarMinSpeed, setWhatIfRoadAeroAerobarMinSpeed] = useState(initialWorkoutData?.parameters?.what_if_road_aero_aerobar_min_speed || 18);
  const [whatIfRoadAeroAerobarMaxSpeed, setWhatIfRoadAeroAerobarMaxSpeed] = useState(initialWorkoutData?.parameters?.what_if_road_aero_aerobar_max_speed || 40);
  const [whatIfRoadAeroDropsMinSpeed, setWhatIfRoadAeroDropsMinSpeed] = useState(initialWorkoutData?.parameters?.what_if_road_aero_drops_min_speed || 40);
  const [whatIfGravelDropsMinSpeed, setWhatIfGravelDropsMinSpeed] = useState(initialWorkoutData?.parameters?.what_if_gravel_drops_min_speed || 30);

  const [airDensity, setAirDensity] = useState(initialWorkoutData?.parameters?.air_density || 1.225);
  const [rollingResistance, setRollingResistance] = useState(initialWorkoutData?.parameters?.rolling_resistance || 0.004);
  const [cdaTtAero, setCdaTtAero] = useState(initialWorkoutData?.parameters?.cda_tt_aero || 0.24);
  const [cdaTtBase, setCdaTtBase] = useState(initialWorkoutData?.parameters?.cda_tt_base || 0.38);
  const [cdaRoadHoods, setCdaRoadHoods] = useState(initialWorkoutData?.parameters?.cda_road_hoods || 0.40);
  const [cdaRoadDrops, setCdaRoadDrops] = useState(initialWorkoutData?.parameters?.cda_road_drops || 0.32);
  const [cdaRoadAeroHoods, setCdaRoadAeroHoods] = useState(initialWorkoutData?.parameters?.cda_road_aero_hoods || 0.40);
  const [cdaRoadAeroAerobar, setCdaRoadAeroAerobar] = useState(initialWorkoutData?.parameters?.cda_road_aero_aerobar || 0.30);
  const [cdaRoadAeroDrops, setCdaRoadAeroDrops] = useState(initialWorkoutData?.parameters?.cda_road_aero_drops || 0.32);
  const [cdaGravelHoods, setCdaGravelHoods] = useState(initialWorkoutData?.parameters?.cda_gravel_hoods || 0.42);
  const [cdaGravelDrops, setCdaGravelDrops] = useState(initialWorkoutData?.parameters?.cda_gravel_drops || 0.34);

  // Units state to make component reactive to unit changes
  const [units, setUnits] = useState(() => getUnits());

  // Function to calculate rolling resistance based on bike type and terrain
  const calculateRollingResistance = (bikeType, terrainType, pavedPercentage) => {
    // For road, road_aero and tt bikes: terrain-aware
    if (bikeType === 'road' || bikeType === 'road_aero' || bikeType === 'tt') {
      if (terrainType === 'paved') {
        return 0.004;
      } else if (terrainType === 'gravel') {
        return 0.010;  // Road/TT bike on pure gravel
      } else if (terrainType === 'mixed') {
        // Weighted average: (paved% * 0.004) + (gravel% * 0.010)
        const pavedFraction = pavedPercentage / 100;
        const gravelFraction = 1 - pavedFraction;
        return (pavedFraction * 0.004) + (gravelFraction * 0.010);
      }
    }

    // For gravel bike:
    if (bikeType === 'gravel') {
      if (terrainType === 'paved') {
        return 0.005;
      } else if (terrainType === 'gravel') {
        return 0.012;
      } else if (terrainType === 'mixed') {
        // Weighted average: (paved% * 0.005) + (gravel% * 0.012)
        const pavedFraction = pavedPercentage / 100;
        const gravelFraction = 1 - pavedFraction;
        return (pavedFraction * 0.005) + (gravelFraction * 0.012);
      }
    }

    return 0.004; // fallback
  };

  // Function to validate terrain compatibility between FIT and GPX
  const getTerrainCompatibility = (fitTerrainType, fitPavedPct, gpxTerrainType, gpxPavedPct) => {
    // Get paved percentages
    const fitPaved = fitTerrainType === 'paved' ? 100 :
                     fitTerrainType === 'gravel' ? 0 :
                     fitPavedPct;
    const gpxPaved = gpxTerrainType === 'paved' ? 100 :
                     gpxTerrainType === 'gravel' ? 0 :
                     gpxPavedPct;

    const difference = Math.abs(fitPaved - gpxPaved);

    // Pure terrain mismatches (0% vs 100% or 100% vs 0%)
    if ((fitPaved === 100 && gpxPaved === 0) || (fitPaved === 0 && gpxPaved === 100)) {
      return {
        type: 'block',
        message: `❌ Terrain incompatible: Your workout was on ${fitPaved === 100 ? 'paved roads' : 'gravel'}, but this route is ${gpxPaved === 100 ? 'paved' : 'gravel'}. Rolling resistance differs significantly. Please select a FIT file from similar terrain.`
      };
    }

    // Large difference in mixed terrain (>30%)
    if (difference > 30) {
      return {
        type: 'block',
        message: `❌ Terrain incompatible: Your workout was ${fitPaved}% paved, but this route is ${gpxPaved}% paved (${difference.toFixed(0)}% difference). Rolling resistance differs too much for accurate predictions.`
      };
    }

    // Pure to mostly similar mixed (within ±30%)
    if ((fitPaved === 100 && gpxPaved >= 70 && gpxPaved < 100) ||
        (fitPaved === 0 && gpxPaved > 0 && gpxPaved <= 30)) {
      return {
        type: 'warning',
        message: `⚠️ Terrain warning: Your workout was ${fitPaved === 100 ? 'on paved roads (100%)' : 'on gravel (0%)'}, but this route is ${gpxPaved}% paved. Rolling resistance will be adjusted. Accuracy may be slightly reduced.`
      };
    }

    // Mixed terrain with moderate difference (10-30%)
    if (difference > 10 && difference <= 30) {
      return {
        type: 'warning',
        message: `⚠️ Terrain notice: Your workout was ${fitPaved}% paved, but this route is ${gpxPaved}% paved (${difference.toFixed(0)}% difference). Rolling resistance will be adjusted accordingly.`
      };
    }

    // Compatible (difference <= 10%)
    return {
      type: 'compatible',
      message: null
    };
  };

  // Course prediction state
  const [gpxFile, setGpxFile] = useState(null);
  const [gpxFileName, setGpxFileName] = useState('');
  const [gpxTerrainType, setGpxTerrainType] = useState('paved'); // Terrain for GPX route
  const [gpxTerrainPavedPercentage, setGpxTerrainPavedPercentage] = useState(100); // 0-100
  const [isPredicting, setIsPredicting] = useState(false);
  const [predictionResult, setPredictionResult] = useState(null);
  const [powerRatio, setPowerRatio] = useState(initialWorkoutData?.power_ratio || 1.0);
  
  // GPX file history state
  const [gpxPickerOpen, setGpxPickerOpen] = useState(false);

  const [showShareDialog, setShowShareDialog] = useState(false);

  // More Actions panel state
  const [showMoreActionsPanel, setShowMoreActionsPanel] = useState(false);

  // Compare drawer state
  const [compareDrawerOpen, setCompareDrawerOpen] = useState(false);

  // Fetch shared analysis when in share mode
  useEffect(() => {
    if (!shareId) return;
    (async () => {
      try {
        setShareLoading(true);
        setShareError('');
        const data = await getSharedBikeAnalysis(shareId);
        setShareInfo(data);
        setWorkoutData(data.workout_data);
      } catch (err) {
        setShareError(err.response?.data?.detail || 'Failed to load shared analysis');
      } finally {
        setShareLoading(false);
      }
    })();
  }, [shareId]);

  const handleUnshare = async () => {
    try {
      setUnshareLoading(true);
      await unshareBikeAnalysis(shareId);
      setUnshareDialogOpen(false);
      navigate('/bike');
    } catch {
      alert('Failed to unshare analysis');
    } finally {
      setUnshareLoading(false);
    }
  };

  // Update powerRatio when workoutData changes
  useEffect(() => {
    if (workoutData?.power_ratio !== undefined && workoutData?.power_ratio !== null) {
      setPowerRatio(workoutData.power_ratio);
    }
  }, [workoutData?.power_ratio]);

  // Auto-update rolling resistance when bike type, terrain type, or paved percentage changes
  useEffect(() => {
    const newCrr = calculateRollingResistance(bikeType, terrainType, terrainPavedPercentage);
    setRollingResistance(parseFloat(newCrr.toFixed(4)));
  }, [bikeType, terrainType, terrainPavedPercentage]);

  // Force terrain to 'paved' for road_aero and tt bikes only
  useEffect(() => {
    if (bikeType === 'road_aero' || bikeType === 'tt') {
      if (terrainType !== 'paved') {
        setTerrainType('paved');
        setTerrainPavedPercentage(100);
      }
    }
  }, [bikeType, terrainType]);

  // Listen for unit changes
  useEffect(() => {
    const handleUnitsChange = (e) => {
      setUnits(e.detail || getUnits());
    };

    window.addEventListener('unitsChanged', handleUnitsChange);

    return () => {
      window.removeEventListener('unitsChanged', handleUnitsChange);
    };
  }, []);

  // Dynamic labels based on current units
  const distanceLabel = units === 'imperial' ? 'mi' : 'km';
  const speedLabel = units === 'imperial' ? 'mph' : 'km/h';
  const elevationLabel = units === 'imperial' ? 'ft' : 'm';
  const weightLabel = units === 'imperial' ? 'lb' : 'kg';

  // Update bike weight when bike type changes (load from user parameters)
  useEffect(() => {
    const loadBikeWeightForType = async () => {
      try {
        const params = await getUserParameters(user !== null);

        if (bikeType === 'tt' && params.tt_bike_weight) {
          console.log('WorkoutAnalysisV2: Setting TT bike weight from user params:', params.tt_bike_weight);
          setBikeWeight(params.tt_bike_weight);
        } else if (bikeType === 'road' && params.road_bike_weight) {
          console.log('WorkoutAnalysisV2: Setting road bike weight from user params:', params.road_bike_weight);
          setBikeWeight(params.road_bike_weight);
        }
      } catch (error) {
        console.error('Error loading bike weight for bike type:', error);
      }
    };

    loadBikeWeightForType();
  }, [bikeType, user]);

  // Update What-If bike weight when What-If bike type changes (load from user parameters)
  useEffect(() => {
    const loadWhatIfBikeWeightForType = async () => {
      try {
        const params = await getUserParameters(user !== null);

        if (whatIfBikeType === 'tt' && params.tt_bike_weight) {
          console.log('WorkoutAnalysisV2: Setting What-If TT bike weight from user params:', params.tt_bike_weight);
          setWhatIfBikeWeight(params.tt_bike_weight);
        } else if (whatIfBikeType === 'road' && params.road_bike_weight) {
          console.log('WorkoutAnalysisV2: Setting What-If road bike weight from user params:', params.road_bike_weight);
          setWhatIfBikeWeight(params.road_bike_weight);
        }
      } catch (error) {
        console.error('Error loading What-If bike weight for bike type:', error);
      }
    };

    loadWhatIfBikeWeightForType();
  }, [whatIfBikeType, user]);

  // Function to fetch saved GPX files
  const resetGpxState = () => {
    setPredictionResult(null);
    setPowerRatio(1.0);
    setGpxTerrainType('paved');
    setGpxTerrainPavedPercentage(100);
    if (workoutData) setWorkoutData({ ...workoutData, power_ratio: null });
  };

  const handleGpxPickerSelect = async (source) => {
    if (source.type === 'upload') {
      setGpxFile(source.file);
      setGpxFileName(source.file.name);
      resetGpxState();
    } else if (source.type === 'myFile') {
      try {
        const blob = await downloadUserFile('gpx', source.fileId);
        const file = new File([blob], source.record.filename, { type: 'application/gpx+xml' });
        setGpxFile(file);
        setGpxFileName(source.record.filename);
        resetGpxState();
        setGpxTerrainType(source.record.terrain_type || 'paved');
        setGpxTerrainPavedPercentage(source.record.terrain_paved_percentage || 100);
      } catch {
        alert('Failed to load GPX file');
      }
    } else if (source.type === 'community') {
      setGpxFile(source.file);
      setGpxFileName(source.name);
      resetGpxState();
    } else if (source.type === 'example') {
      try {
        const response = await fetch(process.env.PUBLIC_URL + '/data/IMFranceNice.gpx');
        const blob = await response.blob();
        const file = new File([blob], 'IMFranceNice.gpx', { type: 'application/gpx+xml' });
        setGpxFile(file);
        setGpxFileName('IMFranceNice.gpx');
        resetGpxState();
      } catch {
        alert('Failed to load example GPX file');
      }
    }
  };

  const chunks = workoutData?.chunks || [];
  const parameters = workoutData?.parameters;
  const total_distance = workoutData?.total_distance ?? 0;
  const total_duration = workoutData?.total_duration ?? 0;
  const avg_speed = workoutData?.avg_speed ?? 0;
  const avg_power = workoutData?.avg_power ?? 0;
  const total_elevation_gain = workoutData?.total_elevation_gain ?? 0;
  const total_elevation_loss = workoutData?.total_elevation_loss ?? 0;

  // Reanalyze function
  const handleReanalyze = async () => {
    if (!storedFile) {
      alert('No file available for reanalysis. Please upload a new file.');
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        bikeType,
        riderWeight: parseFloat(riderWeight),
        bikeWeight: parseFloat(bikeWeight),
        ttAeroMinSpeed: parseFloat(ttAeroMinSpeed),
        ttAeroMaxSpeed: parseFloat(ttAeroMaxSpeed),
        roadDropsMinSpeed: parseFloat(roadDropsMinSpeed),
        roadAeroAerobarMinSpeed: parseFloat(roadAeroAerobarMinSpeed),
        roadAeroAerobarMaxSpeed: parseFloat(roadAeroDropsMinSpeed), // Use drops min speed as aerobar max speed
        roadAeroDropsMinSpeed: parseFloat(roadAeroDropsMinSpeed),
        gravelDropsMinSpeed: parseFloat(gravelDropsMinSpeed),
        terrainType,
        terrainPavedPercentage: parseFloat(terrainPavedPercentage),
        whatIfBikeType,
        whatIfRiderWeight: parseFloat(whatIfRiderWeight),
        whatIfBikeWeight: parseFloat(whatIfBikeWeight),
        whatIfTtAeroMinSpeed: parseFloat(whatIfTtAeroMinSpeed),
        whatIfTtAeroMaxSpeed: parseFloat(whatIfTtAeroMaxSpeed),
        whatIfRoadDropsMinSpeed: parseFloat(whatIfRoadDropsMinSpeed),
        whatIfRoadAeroAerobarMinSpeed: parseFloat(whatIfRoadAeroAerobarMinSpeed),
        whatIfRoadAeroAerobarMaxSpeed: parseFloat(whatIfRoadAeroDropsMinSpeed), // Use drops min speed as aerobar max speed
        whatIfRoadAeroDropsMinSpeed: parseFloat(whatIfRoadAeroDropsMinSpeed),
        whatIfGravelDropsMinSpeed: parseFloat(whatIfGravelDropsMinSpeed),
        airDensity: parseFloat(airDensity),
        rollingResistance: parseFloat(rollingResistance),
        cdaTtAero: parseFloat(cdaTtAero),
        cdaTtBase: parseFloat(cdaTtBase),
        cdaRoadHoods: parseFloat(cdaRoadHoods),
        cdaRoadDrops: parseFloat(cdaRoadDrops),
        cdaRoadAeroHoods: parseFloat(cdaRoadAeroHoods),
        cdaRoadAeroAerobar: parseFloat(cdaRoadAeroAerobar),
        cdaRoadAeroDrops: parseFloat(cdaRoadAeroDrops),
        cdaGravelHoods: parseFloat(cdaGravelHoods),
        cdaGravelDrops: parseFloat(cdaGravelDrops),
        reusedFileId: reusedFileId  // Include file ID to update existing entry
      };

      // Add power ratio if GPX file is present AND we have a previously calculated value
      // Don't send it if user changed the GPX file (workoutData.power_ratio will be null)
      // This forces the backend to compute a new power ratio
      if (gpxFile && workoutData?.power_ratio) {
        params.powerRatio = parseFloat(powerRatio);
      }

      // Add GPX terrain parameters if GPX file is selected
      if (gpxFile) {
        params.gpxTerrainType = gpxTerrainType;
        params.gpxTerrainPavedPercentage = parseFloat(gpxTerrainPavedPercentage);
      }

      // Include GPX file if selected
      const result = await uploadFitFile(storedFile, params, gpxFile);
      setWorkoutData(result);
      setBikePlanSegments(null);
      setPlanSelectedSegment(null);
      setPlanChartZoom(null);

      // Clear parameters cache so next load fetches updated values from backend
      clearParametersCache();
      console.log('Parameters cache cleared after reanalysis');
    } catch (error) {
      console.error('Error reanalyzing:', error);
      alert('Failed to reanalyze. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to format time difference in seconds to min:sec
  const formatTimeDiff = (seconds) => {
    const absSeconds = Math.abs(seconds);
    const minutes = Math.floor(absSeconds / 60);
    const secs = Math.round(absSeconds % 60);
    const sign = seconds < 0 ? '-' : '+';
    return `${sign}${minutes}:${secs.toString().padStart(2, '0')}`;
  };



  if (shareLoading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (shareError) {
    return (
      <Box m="20px">
        <Alert severity="error">{shareError}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/bike')}>Go to Dashboard</Button>
      </Box>
    );
  }

  if (!workoutData) {
    return (
      <Box m="20px">
        <Typography variant="h4" color={colors.grey[100]}>
          {t('noWorkoutData', 'No workout data available')}
        </Typography>
      </Box>
    );
  }

  const zeroSpeedSegments = chunks.filter(chunk => chunk.avg_speed < 1).length;

  return (
    <Box m={isMobile ? "2px" : "20px"}  display="flex" justifyContent="center" sx={{ cursor: isLoading ? 'wait' : 'default' }}>
      <Box maxWidth="1200px" width="100%" >
        
        
        
        {/* Share mode banner */}
        {shareInfo && (
          <Paper elevation={2} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ShareIcon sx={{ color: colors.blueAccent[400], fontSize: 18 }} />
            <Typography variant="body2" color={colors.grey[300]}>
              {shareInfo.display_name && <strong>{shareInfo.display_name} · </strong>}
              {shareInfo.is_anonymous ? 'Anonymous share' : `Shared by ${shareInfo.owner_name || 'Unknown'}`}
              {shareInfo.shared_at && ` · ${new Date(shareInfo.shared_at).toLocaleDateString()}`}
              {shareInfo.view_count > 0 && ` · ${shareInfo.view_count} views`}
            </Typography>
            {shareInfo.description && (
              <Typography variant="body2" color={colors.grey[400]} sx={{ width: '100%' }}>
                {shareInfo.description}
              </Typography>
            )}
            {shareInfo.is_owner && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                sx={{ ml: 'auto' }}
                onClick={() => setUnshareDialogOpen(true)}
              >
                Unshare
              </Button>
            )}
          </Paper>
        )}

        <Box mb={4} display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={2} sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ width: isMobile ? '100%' : 'auto', overflow: 'hidden' }}>
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold" color={colors.grey[100]} mb={1}>
              {shareInfo?.display_name || t('workoutAnalysisTitle', 'Workout Analysis')}
            </Typography>
            <Typography variant={isMobile ? "body1" : "h5"} color={colors.grey[300]} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'nowrap' : 'normal' }}>
              {storedFile?.name || shareInfo?.filename || t('segmentsLabel', { count: chunks.length, defaultValue: 'Segments: {{count}}' })}
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={isMobile ? 1 : 2} sx={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            {/* Show traceability button only when GPX analysis data is available and user is admin */}
            {workoutData?.gpx_segments && workoutData?.fit_chunks && user?.is_admin && (
              <Button
                variant="contained"
                startIcon={<AnalyticsIcon />}
                onClick={() => navigate('/traceability', {
                  state: {
                    gpxSegments: workoutData.gpx_segments,
                    fitChunks: workoutData.fit_chunks,
                    workoutData: workoutData
                  }
                })}
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  '&:hover': {
                    backgroundColor: colors.greenAccent[700]
                  }
                }}
              >
                View GPX-FIT Analysis
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={!isMobile && <MoreHorizIcon />}
              onClick={() => setShowMoreActionsPanel(true)}
              sx={{
                color: colors.grey[100],
                borderColor: colors.primary[400],
                minWidth: isMobile ? 'auto' : undefined,
                px: isMobile ? 1 : undefined,
                '&:hover': {
                  borderColor: colors.primary[300],
                  backgroundColor: colors.primary[700]
                }
              }}
            >
              {isMobile ? <MoreHorizIcon /> : t('moreActions', "Let's train")}
            </Button>
            {!shareId && workoutData?.analysis_session_id && !!user && (
              <Button
                variant="outlined"
                startIcon={!isMobile && <ShareIcon />}
                onClick={() => setShowShareDialog(true)}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.primary[400],
                  minWidth: isMobile ? 'auto' : undefined,
                  px: isMobile ? 1 : undefined,
                  '&:hover': {
                    borderColor: colors.primary[300],
                    backgroundColor: colors.primary[700]
                  }
                }}
              >
                {isMobile ? <ShareIcon /> : t('share', 'Share')}
              </Button>
            )}
            {!shareId && workoutData?.analysis_session_id && (
              <Button
                variant="outlined"
                startIcon={!isMobile && <CompareArrowsIcon />}
                onClick={() => setCompareDrawerOpen(true)}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.primary[400],
                  minWidth: isMobile ? 'auto' : undefined,
                  px: isMobile ? 1 : undefined,
                  '&:hover': {
                    borderColor: colors.primary[300],
                    backgroundColor: colors.primary[700]
                  }
                }}
              >
                {isMobile ? <CompareArrowsIcon /> : 'Compare'}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate('/bike')}
              sx={{
                color: colors.grey[100],
                borderColor: colors.primary[400],
                minWidth: isMobile ? 'auto' : undefined,
                px: isMobile ? 2 : undefined,
                fontSize: isMobile ? '0.75rem' : undefined,
                '&:hover': {
                  borderColor: colors.primary[300],
                  backgroundColor: colors.primary[700]
                }
              }}
            >
              {isMobile ? t('dashboardTitle', 'Biking Analysis') : t('backToDashboard', 'Back to Dashboard')}
            </Button>
          </Box>
        </Box>

        {/* Bike Configuration Info - Editable */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 2 : 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: isMobile ? 2 : 3,
              backgroundColor: colors.grey[800],
              borderRadius: "8px",
              height: '100%'
            }}
          >
            <Typography variant="h5" fontWeight="bold" color={colors.blueAccent[500]} mb={2}>
              {t('actualBikeSetup', 'Actual Bike Setup')}
            </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.grey[300] }}>{t('bikeType', 'Bike Type')}</InputLabel>
              <Select
                value={bikeType}
                onChange={(e) => setBikeType(e.target.value)}
                label={t('bikeType', 'Bike Type')}
                sx={{
                  color: colors.grey[100],
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              >
                <MenuItem value="road">{t('roadBike', 'Road Bike')}</MenuItem>
                <MenuItem value="road_aero">{t('roadBikeAero', 'Road Bike with Aerobars')}</MenuItem>
                <MenuItem value="tt">{t('timeTrialBike', 'Time Trial Bike')}</MenuItem>
                <MenuItem value="gravel">{t('gravelBike', 'Gravel Bike')}</MenuItem>
              </Select>
            </FormControl>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                label={`${t('riderWeight', 'Rider Weight')} (${weightLabel})`}
                type="number"
                value={Math.round(convertWeight(riderWeight, units) * 4) / 4}
                onChange={(e) => setRiderWeight(units === 'imperial' ? parseFloat(e.target.value) / 2.20462 : parseFloat(e.target.value))}
                size="small"
                inputProps={{ step: "0.25" }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputLabel-root': { color: colors.grey[300] },
                  '& .MuiInputBase-input': { color: colors.grey[100] },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              />
              <TextField
                label={`${t('bikeWeight', 'Bike Weight')} (${weightLabel})`}
                type="number"
                value={Math.round(convertWeight(bikeWeight, units) * 4) / 4}
                onChange={(e) => setBikeWeight(units === 'imperial' ? parseFloat(e.target.value) / 2.20462 : parseFloat(e.target.value))}
                size="small"
                inputProps={{ step: "0.25" }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputLabel-root': { color: colors.grey[300] },
                  '& .MuiInputBase-input': { color: colors.grey[100] },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              />
              <Box display="flex" flexDirection="column" alignItems="center" minWidth={isMobile ? "80px" : "100px"}>
                <Typography variant="caption" color={colors.grey[400]}>
                  {t('totalLabel', 'Total')}
                </Typography>
                <Typography variant="body1" color={colors.grey[100]} fontWeight="bold">
                  {convertWeight((parseFloat(riderWeight) + parseFloat(bikeWeight)), getUnits()).toFixed(1)} {weightLabel}
                </Typography>
              </Box>
            </Box>
            {bikeType === 'tt' && (
              <>
                <Box display="flex" gap={1}>
                  <TextField
                    label={`${t('aeroMinSpeed', 'Aero Position Min Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(ttAeroMinSpeed, units))}
                    onChange={(e) => setTtAeroMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={`${t('aeroMaxSpeed', 'Aero Position Max Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(ttAeroMaxSpeed, units))}
                    onChange={(e) => setTtAeroMaxSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaTtAero', 'CdA TT Aero')}
                    type="number"
                    value={cdaTtAero}
                    onChange={(e) => setCdaTtAero(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaTtBase', 'CdA TT Base')}
                    type="number"
                    value={cdaTtBase}
                    onChange={(e) => setCdaTtBase(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
            {bikeType === 'road' && (
              <>
                <TextField
                  label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                  type="number"
                  value={Math.round(convertSpeed(roadDropsMinSpeed, units))}
                  onChange={(e) => setRoadDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: colors.grey[300] },
                    '& .MuiInputBase-input': { color: colors.grey[100] },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                  }}
                />
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaRoadHoods', 'CdA Road Hoods')}
                    type="number"
                    value={cdaRoadHoods}
                    onChange={(e) => setCdaRoadHoods(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaRoadDrops', 'CdA Road Drops')}
                    type="number"
                    value={cdaRoadDrops}
                    onChange={(e) => setCdaRoadDrops(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
            {bikeType === 'road_aero' && (
              <>
                <Box display="flex" gap={1}>
                  <TextField
                    label={`${t('aerobarMinSpeed', 'Aerobar Min Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(roadAeroAerobarMinSpeed, units))}
                    onChange={(e) => setRoadAeroAerobarMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(roadAeroDropsMinSpeed, units))}
                    onChange={(e) => setRoadAeroDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaRoadAeroHoods', 'CdA Road Aero Hoods')}
                    type="number"
                    value={cdaRoadAeroHoods}
                    onChange={(e) => setCdaRoadAeroHoods(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaRoadAeroAerobar', 'CdA Road Aero Aerobar')}
                    type="number"
                    value={cdaRoadAeroAerobar}
                    onChange={(e) => setCdaRoadAeroAerobar(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaRoadAeroDrops', 'CdA Road Aero Drops')}
                    type="number"
                    value={cdaRoadAeroDrops}
                    onChange={(e) => setCdaRoadAeroDrops(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
            {bikeType === 'gravel' && (
              <>
                <TextField
                  label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                  type="number"
                  value={Math.round(convertSpeed(gravelDropsMinSpeed, units))}
                  onChange={(e) => setGravelDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: colors.grey[300] },
                    '& .MuiInputBase-input': { color: colors.grey[100] },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                  }}
                />
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaGravelHoods', 'CdA Gravel Hoods')}
                    type="number"
                    value={cdaGravelHoods}
                    onChange={(e) => setCdaGravelHoods(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaGravelDrops', 'CdA Gravel Drops')}
                    type="number"
                    value={cdaGravelDrops}
                    onChange={(e) => setCdaGravelDrops(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: isMobile ? 2 : 3,
              backgroundColor: colors.grey[800],
              borderRadius: "8px",
              height: '100%'
            }}
          >
            <Typography variant="h5" fontWeight="bold" color={colors.greenAccent[500]} mb={2}>
              {t('whatIfScenario', 'What-If Scenario')}
            </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.grey[300] }}>{t('bikeType', 'Bike Type')}</InputLabel>
              <Select
                value={whatIfBikeType}
                onChange={(e) => setWhatIfBikeType(e.target.value)}
                label={t('bikeType', 'Bike Type')}
                sx={{
                  color: colors.grey[100],
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              >
                <MenuItem value="road">{t('roadBike', 'Road Bike')}</MenuItem>
                <MenuItem value="road_aero">{t('roadBikeAero', 'Road Bike with Aerobars')}</MenuItem>
                <MenuItem value="tt">{t('timeTrialBike', 'Time Trial Bike')}</MenuItem>
                <MenuItem value="gravel">{t('gravelBike', 'Gravel Bike')}</MenuItem>
              </Select>
            </FormControl>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                label={`${t('riderWeight', 'Rider Weight')} (${weightLabel})`}
                type="number"
                value={Math.round(convertWeight(whatIfRiderWeight, units) * 4) / 4}
                onChange={(e) => setWhatIfRiderWeight(units === 'imperial' ? parseFloat(e.target.value) / 2.20462 : parseFloat(e.target.value))}
                size="small"
                inputProps={{ step: "0.25" }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputLabel-root': { color: colors.grey[300] },
                  '& .MuiInputBase-input': { color: colors.grey[100] },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              />
              <TextField
                label={`${t('bikeWeight', 'Bike Weight')} (${weightLabel})`}
                type="number"
                value={Math.round(convertWeight(whatIfBikeWeight, units) * 4) / 4}
                onChange={(e) => setWhatIfBikeWeight(units === 'imperial' ? parseFloat(e.target.value) / 2.20462 : parseFloat(e.target.value))}
                size="small"
                inputProps={{ step: "0.25" }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputLabel-root': { color: colors.grey[300] },
                  '& .MuiInputBase-input': { color: colors.grey[100] },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                }}
              />
              <Box display="flex" flexDirection="column" alignItems="center" minWidth={isMobile ? "100px" : "140px"}>
                <Typography variant="caption" color={colors.grey[400]}>
                  {t('totalLabel', 'Total')}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body1" color={colors.grey[100]} fontWeight="bold">
                    {convertWeight((parseFloat(whatIfRiderWeight) + parseFloat(whatIfBikeWeight)), units).toFixed(1)} {weightLabel}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={(parseFloat(whatIfRiderWeight) + parseFloat(whatIfBikeWeight)) < (parseFloat(riderWeight) + parseFloat(bikeWeight)) ? colors.greenAccent[400] : colors.redAccent[400]}
                    fontWeight="bold"
                  >
                    ({convertWeight((parseFloat(whatIfRiderWeight) + parseFloat(whatIfBikeWeight)) - (parseFloat(riderWeight) + parseFloat(bikeWeight)), units).toFixed(1) > 0 ? '+' : ''}
                    {convertWeight((parseFloat(whatIfRiderWeight) + parseFloat(whatIfBikeWeight)) - (parseFloat(riderWeight) + parseFloat(bikeWeight)), units).toFixed(1)})
                  </Typography>
                </Box>
              </Box>
            </Box>
            {whatIfBikeType === 'tt' && (
              <>
                <Box display="flex" gap={1}>
                  <TextField
                    label={`${t('aeroMinSpeed', 'Aero Position Min Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(whatIfTtAeroMinSpeed, units))}
                    onChange={(e) => setWhatIfTtAeroMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={`${t('aeroMaxSpeed', 'Aero Position Max Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(whatIfTtAeroMaxSpeed, units))}
                    onChange={(e) => setWhatIfTtAeroMaxSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaTtAero', 'CdA TT Aero')}
                    type="number"
                    value={cdaTtAero}
                    onChange={(e) => setCdaTtAero(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaTtBase', 'CdA TT Base')}
                    type="number"
                    value={cdaTtBase}
                    onChange={(e) => setCdaTtBase(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{ 
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
            {whatIfBikeType === 'road' && (
              <>
                <TextField
                  label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                  type="number"
                  value={Math.round(convertSpeed(whatIfRoadDropsMinSpeed, units))}
                  onChange={(e) => setWhatIfRoadDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: colors.grey[300] },
                    '& .MuiInputBase-input': { color: colors.grey[100] },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                  }}
                />
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaRoadHoods', 'CdA Road Hoods')}
                    type="number"
                    value={cdaRoadHoods}
                    onChange={(e) => setCdaRoadHoods(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaRoadDrops', 'CdA Road Drops')}
                    type="number"
                    value={cdaRoadDrops}
                    onChange={(e) => setCdaRoadDrops(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
            {whatIfBikeType === 'road_aero' && (
              <>
                <Box display="flex" gap={1}>
                  <TextField
                    label={`${t('aerobarMinSpeed', 'Aerobar Min Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(whatIfRoadAeroAerobarMinSpeed, units))}
                    onChange={(e) => setWhatIfRoadAeroAerobarMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                    type="number"
                    value={Math.round(convertSpeed(whatIfRoadAeroDropsMinSpeed, units))}
                    onChange={(e) => setWhatIfRoadAeroDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaRoadAeroHoods', 'CdA Road Aero Hoods')}
                    type="number"
                    value={cdaRoadAeroHoods}
                    onChange={(e) => setCdaRoadAeroHoods(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaRoadAeroAerobar', 'CdA Road Aero Aerobar')}
                    type="number"
                    value={cdaRoadAeroAerobar}
                    onChange={(e) => setCdaRoadAeroAerobar(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaRoadAeroDrops', 'CdA Road Aero Drops')}
                    type="number"
                    value={cdaRoadAeroDrops}
                    onChange={(e) => setCdaRoadAeroDrops(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
            {whatIfBikeType === 'gravel' && (
              <>
                <TextField
                  label={`${t('dropsMinSpeed', 'Drops Position Min Speed')} (${speedLabel})`}
                  type="number"
                  value={Math.round(convertSpeed(whatIfGravelDropsMinSpeed, units))}
                  onChange={(e) => setWhatIfGravelDropsMinSpeed(units === 'imperial' ? parseFloat(e.target.value) / 0.621371 : parseFloat(e.target.value))}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: colors.grey[300] },
                    '& .MuiInputBase-input': { color: colors.grey[100] },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                  }}
                />
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('cdaGravelHoods', 'CdA Gravel Hoods')}
                    type="number"
                    value={cdaGravelHoods}
                    onChange={(e) => setCdaGravelHoods(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                  <TextField
                    label={t('cdaGravelDrops', 'CdA Gravel Drops')}
                    type="number"
                    value={cdaGravelDrops}
                    onChange={(e) => setCdaGravelDrops(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiInputLabel-root': { color: colors.grey[300] },
                      '& .MuiInputBase-input': { color: colors.grey[100] },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Paper>
        </Grid>
      </Grid>

      {/* Advanced Physics Settings */}
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 3,
          mb: isMobile ? 2 : 4,
          backgroundColor: colors.grey[800],
          borderRadius: "8px"
        }}
      >
        <Typography variant="h5" fontWeight="bold" color={colors.warnings.orange} mb={2}>
          {t('terrainAndAdvancedSettings', 'Terrain & Advanced Settings')}
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={2} mb={2}>
          <FormControl fullWidth size="small" disabled={bikeType === 'road_aero' || bikeType === 'tt'}>
            <InputLabel sx={{ color: colors.grey[300] }}>{t('terrainType', 'Terrain Type')}</InputLabel>
            <Select
              value={terrainType}
              onChange={(e) => {
                setTerrainType(e.target.value);
                // Set default percentages based on terrain type
                if (e.target.value === 'paved') setTerrainPavedPercentage(100);
                else if (e.target.value === 'gravel') setTerrainPavedPercentage(0);
                else setTerrainPavedPercentage(50); // mixed
              }}
              label="Terrain Type"
              sx={{
                color: colors.grey[100],
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[600],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '.MuiSvgIcon-root': {
                  color: colors.grey[300],
                }
              }}
            >
              <MenuItem value="paved">{t('terrainPaved', 'Paved (100%)')}</MenuItem>
              <MenuItem value="mixed">{t('terrainMixed', 'Mixed (Custom %)')}</MenuItem>
              <MenuItem value="gravel">{t('terrainGravel', 'Gravel (0% paved)')}</MenuItem>
            </Select>
          </FormControl>
          <Box>
            <TextField
              label={t('rollingResistanceCoefficient', 'Rolling Resistance Coefficient')}
              type="number"
              value={rollingResistance}
              onChange={(e) => setRollingResistance(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ step: "0.0001" }}
              sx={{
                '& .MuiInputLabel-root': { color: colors.grey[300] },
                '& .MuiInputBase-input': { color: colors.grey[100] },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
              }}
            />
            <Typography variant="caption" color={colors.grey[400]} mt={0.5} display="block">
              {t('fitWorkoutCrr', 'For FIT workout analysis. GPX predictions use GPX terrain Crr.')}
            </Typography>
          </Box>
        </Box>
        {terrainType === 'mixed' && (
          <Box px={2} mb={2}>
            <Typography variant="body2" color={colors.grey[300]} mb={1}>
              {t('pavedPercentage', 'Paved Surface Percentage')}: {terrainPavedPercentage}%
            </Typography>
            <Slider
              value={terrainPavedPercentage}
              onChange={(e, value) => setTerrainPavedPercentage(value)}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' }
              ]}
              sx={{
                color: colors.blueAccent[500],
                '& .MuiSlider-markLabel': {
                  color: colors.grey[400],
                },
                '& .MuiSlider-mark': {
                  backgroundColor: colors.grey[600],
                }
              }}
            />
          </Box>
        )}
        <Box display="grid" gridTemplateColumns={{ xs: '1fr' }} gap={2}>
          <TextField
            label={t('airDensity', 'Air Density')}
            type="number"
            value={airDensity}
            onChange={(e) => setAirDensity(e.target.value)}
            size="small"
            inputProps={{ step: "0.001" }}
            sx={{
              '& .MuiInputLabel-root': { color: colors.grey[300] },
              '& .MuiInputBase-input': { color: colors.grey[100] },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] }
            }}
          />
        </Box>
      </Paper>

      {/* Course Prediction Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: colors.grey[800],
          borderRadius: "8px"
        }}
      >
        <Typography variant="h5" fontWeight="bold" color={colors.warnings.blue} mb={2}>
          {t('coursePrediction', 'Course Prediction')}
        </Typography>
        <Typography variant="body2" color={colors.grey[300]} mb={2}>
          {t('uploadGpxPrediction', { file: storedFile?.name || storedFile || t('thisWorkout', 'this workout'), defaultValue: 'Upload a GPX file to predict performance on a different course using power characteristics from {{file}}' })}
        </Typography>
        
        <Box display="flex" alignItems="center" gap={1.5} mb={gpxFileName ? 1 : 2} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={<RouteIcon />}
            onClick={() => setGpxPickerOpen(true)}
            sx={{
              textTransform: 'none',
              color: colors.grey[200],
              borderColor: colors.grey[600],
              '&:hover': { borderColor: colors.blueAccent[400] },
            }}
          >
            {gpxFileName ? t('changeCourse', 'Change Course') : t('selectGpxCourse', 'Select GPX Course')}
          </Button>
        </Box>

        {gpxFileName && (
          <>
            <Box mb={2}>
              <Typography variant="body2" color={colors.grey[300]}>
                {t('selectedFile', 'Selected')}: {gpxFileName}
              </Typography>
            </Box>

            {/* GPX Terrain Selector */}
            <Box mb={2}>
              <Typography variant="body2" color={colors.grey[100]} fontWeight="bold" mb={1}>
                {t('gpxRouteTerrain', 'GPX Route Terrain (Optional)')}
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={2}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: colors.grey[300] }}>{t('terrainType', 'Terrain Type')}</InputLabel>
                  <Select
                    value={gpxTerrainType}
                    onChange={(e) => {
                      setGpxTerrainType(e.target.value);
                      // Set default percentages based on terrain type
                      if (e.target.value === 'paved') setGpxTerrainPavedPercentage(100);
                      else if (e.target.value === 'gravel') setGpxTerrainPavedPercentage(0);
                      else setGpxTerrainPavedPercentage(70); // mixed - default to 70% for GPX
                    }}
                    label="Terrain Type"
                    sx={{
                      color: colors.grey[100],
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.grey[600],
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.blueAccent[500],
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.blueAccent[500],
                      },
                      '.MuiSvgIcon-root': {
                        color: colors.grey[300],
                      }
                    }}
                  >
                    <MenuItem value="paved">{t('terrainPaved', 'Paved (100%)')}</MenuItem>
                    <MenuItem value="mixed">{t('terrainMixed', 'Mixed (Custom %)')}</MenuItem>
                    <MenuItem value="gravel">{t('terrainGravel', 'Gravel (0% paved)')}</MenuItem>
                  </Select>
                </FormControl>
                <Box></Box>
              </Box>
              {gpxTerrainType === 'mixed' && (
                <Box mt={2}>
                  <Typography variant="body2" color={colors.grey[300]} mb={1}>
                    {t('pavedPercentage', 'Paved Surface Percentage')}: {gpxTerrainPavedPercentage}%
                  </Typography>
                  <Slider
                    value={gpxTerrainPavedPercentage}
                    onChange={(e, value) => setGpxTerrainPavedPercentage(value)}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' },
                      { value: 75, label: '75%' },
                      { value: 100, label: '100%' }
                    ]}
                    sx={{
                      color: colors.blueAccent[500],
                      '& .MuiSlider-markLabel': {
                        color: colors.grey[400],
                      },
                      '& .MuiSlider-mark': {
                        backgroundColor: colors.grey[600],
                      }
                    }}
                  />
                </Box>
              )}
              <Typography variant="caption" color={colors.grey[400]} mt={1} display="block">
                {t('gpxTerrainHelp', 'Specify the terrain type of the GPX route for accurate predictions. The system will validate terrain compatibility with your FIT workout.')}
              </Typography>
              {/* Show Terrain Comparison if GPX terrain is mixed OR differs from FIT */}
              {(gpxTerrainType === 'mixed' || gpxTerrainType !== terrainType || gpxTerrainPavedPercentage !== terrainPavedPercentage) && (() => {
                const compatibility = getTerrainCompatibility(terrainType, terrainPavedPercentage, gpxTerrainType, gpxTerrainPavedPercentage);
                const isBlock = compatibility.type === 'block';
                const isWarning = compatibility.type === 'warning';

                return (
                  <Box mt={2}>
                    {/* Terrain Comparison Box */}
                    <Box p={2} sx={{
                      backgroundColor: colors.blueAccent[900],
                      borderRadius: '4px',
                      border: `1px solid ${colors.blueAccent[700]}`
                    }}>
                      <Typography variant="body2" color={colors.grey[100]} fontWeight="bold" mb={1}>
                        {t('terrainComparison', 'Terrain Comparison')}:
                      </Typography>
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Box flex={1}>
                          <Typography variant="caption" color={colors.grey[400]}>
                            {t('fitWorkout', 'FIT Workout')}:
                          </Typography>
                          <Typography variant="body2" color={colors.grey[100]}>
                            {terrainType === 'paved' ? t('terrainPaved', 'Paved (100%)') :
                             terrainType === 'gravel' ? t('terrainGravel', 'Gravel (0% paved)') :
                             `${t('terrainMixed', 'Mixed (Custom %)')} (${terrainPavedPercentage}%)`}
                          </Typography>
                          <Typography variant="body2" color={colors.blueAccent[400]} fontWeight="bold">
                            Crr: {calculateRollingResistance(bikeType, terrainType, terrainPavedPercentage).toFixed(4)}
                          </Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="caption" color={colors.grey[400]}>
                            {t('gpxRoute', 'GPX Route')}:
                          </Typography>
                          <Typography variant="body2" color={colors.grey[100]}>
                            {gpxTerrainType === 'paved' ? t('terrainPaved', 'Paved (100%)') :
                             gpxTerrainType === 'gravel' ? t('terrainGravel', 'Gravel (0% paved)') :
                             `${t('terrainMixed', 'Mixed (Custom %)')} (${gpxTerrainPavedPercentage}%)`}
                          </Typography>
                          <Typography variant="body2" color={colors.greenAccent[400]} fontWeight="bold">
                            Crr: {calculateRollingResistance(bikeType, gpxTerrainType, gpxTerrainPavedPercentage).toFixed(4)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Warning/Error Message */}
                    {compatibility.message && (
                      <Box
                        mt={2}
                        p={2}
                        sx={{
                          backgroundColor: isBlock ? colors.redAccent[900] : colors.warnings.orangeBackground || 'rgba(255, 152, 0, 0.1)',
                          borderRadius: '4px',
                          border: `2px solid ${isBlock ? colors.redAccent[500] : colors.warnings.orange}`
                        }}
                      >
                        <Typography
                          variant="body2"
                          color={isBlock ? colors.redAccent[300] : colors.warnings.orange}
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {compatibility.message}
                        </Typography>
                        {isBlock && (
                          <Typography variant="caption" color={colors.grey[400]} mt={1} display="block">
                            {t('terrainBlockHelp', 'Predictions with such different terrains will be highly inaccurate. Please upload a different FIT file or GPX route with similar terrain.')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          </>
        )}

        {/* GPX File History Panel */}
        
        {/* Display and edit power ratio if GPX prediction was used */}
        {workoutData?.power_ratio && (
          <Box mt={2} p={2} sx={{ backgroundColor: colors.grey[700], borderRadius: "4px" }}>
            <Typography variant="body2" color={colors.grey[100]} fontWeight="bold" mb={1}>
              {t('powerRatioApplied', 'Power Ratio Applied')}:
            </Typography>
            <TextField
              label={t('powerRatio', 'Power Ratio')}
              type="number"
              value={powerRatio}
              onChange={(e) => setPowerRatio(e.target.value)}
              inputProps={{ step: 0.01, min: 0.5, max: 1.5 }}
              sx={{ mt: 1, width: '150px' }}
              size="small"
            />
            <Typography variant="caption" color={colors.grey[400]} mt={1} display="block">
              {powerRatio < 1.0 
                ? t('powerRatioDecrease', { percent: ((1 - powerRatio) * 100).toFixed(1), defaultValue: 'Power decreased by {{percent}}% (GPX course is longer than FIT workout)' })
                : t('powerRatioIncrease', { percent: ((powerRatio - 1) * 100).toFixed(1), defaultValue: 'Power increased by {{percent}}% (GPX course is shorter than FIT workout)' })
              }
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Reanalyze Button */}
      <Box display="flex" justifyContent="center" mb={4}>
        <Button
          variant="contained"
          onClick={handleReanalyze}
          disabled={isLoading || !storedFile}
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 30px",
            '&:hover': {
              backgroundColor: colors.blueAccent[800],
            },
            '&:disabled': {
              backgroundColor: colors.grey[700],
              color: colors.grey[500],
            }
          }}
        >
          {isLoading ? <CircularProgress size={24} sx={{ color: colors.grey[100] }} /> : t('reanalyzeWithNewSettings', 'Reanalyze with New Settings')}
        </Button>
      </Box>

      {/* Summary Stats - New Layout */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 2 : 4 }}>
        {/* First row: Distance, Duration, Elevation Gain, Elevation Loss, Twistiness Index */}
        <Grid size={{ xs: 6, md: 2.4 }}>
          <StatBox
            title={t('distance', 'Distance')}
            value={`${(convertDistance(total_distance, units).toFixed(2))} ${distanceLabel}`}
            icon={<DirectionsBikeIcon sx={{ fontSize: "32px" }} />}
            iconColor={colors.chartColors[12]}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <StatBox
            title={t('duration', 'Duration')}
            value={`${Math.floor(total_duration / 3600)}h ${Math.floor((total_duration % 3600) / 60)}m`}
            icon={<TimerIcon sx={{ fontSize: "32px" }} />}
            iconColor={colors.chartColors[0]}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <StatBox
            title={t('elevationGain', 'Elevation Gain')}
            value={`${convertElevation(total_elevation_gain, units).toFixed(0)} ${elevationLabel}`}
            icon={<TerrainIcon sx={{ fontSize: "32px" }} />}
            iconColor={`color-mix(in srgb, ${colors.chartColors[1]} 70%, black)`}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <StatBox
            title={t('elevationLoss', 'Elevation Loss')}
            value={`${convertElevation(total_elevation_loss, units).toFixed(0)} ${elevationLabel}`}
            icon={<TerrainIcon sx={{ fontSize: "32px" }} />}
            iconColor={colors.chartColors[1]}
          />
        </Grid>
        {workoutData?.curvature_index !== undefined && (
          <Grid size={{ xs: 6, md: 2.4 }}>
            <StatBox
              title={t('twistiness', 'Twistiness Index')}
              value={`${t(`twistiness${workoutData.curvature_index}`)} (${workoutData.curvature_index})`}
              icon={<RouteIcon sx={{ fontSize: "32px" }} />}
              iconColor={colors.chartColors[8]}
            />
          </Grid>
        )}

        {/* Second row: Avg Speed, Avg Power, Stamina */}
        <Grid size={{ xs: 6, md: 3 }}>
          <StatBox
            title={t('avgSpeed', 'Avg Speed')}
            value={`${convertSpeed(avg_speed, units).toFixed(1)} ${speedLabel}`}
            icon={<SpeedIcon sx={{ fontSize: "32px" }} />}
            iconColor={colors.chartColors[2]}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatBox
            title={t('avgPower', 'Avg Power')}
            value={avg_power != null ? `${avg_power.toFixed(0)} W` : 'N/A'}
            icon={<BoltIcon sx={{ fontSize: "32px" }} />}
            iconColor={colors.chartColors[4]}
          />
        </Grid>
        {workoutData?.total_paused_time != null && workoutData.total_paused_time >= 0 && (
          <Grid size={{ xs: 6, md: 3 }}>
            <StatBox
              title={t('stoppingTime', 'Stopping Time')}
              value={`${(workoutData.total_paused_time / 60).toFixed(1)} min`}
              subtitle={`${workoutData.num_paused_chunks || 0} ${t('stops', 'stops')}`}
              icon={<PauseIcon sx={{ fontSize: "32px" }} />}
              iconColor={colors.warnings.orange}
            />
          </Grid>
        )}
        {workoutData?.stamina_score != null && workoutData?.stamina_index != null && (
          <Grid size={{ xs: 6, md: 3 }}>
            <StatBox
              title={t('overallStamina', 'Overall Stamina')}
              value={`${workoutData.stamina_score.toFixed(1)}%`}
              icon={<BatteryChargingFullIcon sx={{ fontSize: "32px" }} />}
              iconColor={
                workoutData.stamina_index === 5 ? colors.greenAccent[500] :
                workoutData.stamina_index === 4 ? colors.greenAccent[600] :
                workoutData.stamina_index === 3 ? colors.warnings.orange :
                colors.redAccent[500]
              }
            />
          </Grid>
        )}
        {/* Third row: What-If Analysis */}
        <Grid size={{ xs: 12 }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <WhatIfSummaryBox chunks={chunks} workoutData={workoutData} />
          </Box>
        </Grid>
      </Grid>

      {/* Analysis Type Tabs */}
      <Paper
        elevation={3}
        sx={{
          backgroundColor: colors.grey[800],
          borderRadius: "8px",
          mb: 3,
          overflow: 'hidden',
          width: '100%'
        }}
      >
        <Tabs
          value={analysisType}
          onChange={(e, newValue) => { setAnalysisType(newValue); setActiveClimbRange(null); setActiveDescentRange(null); }}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              color: colors.grey[300],
              fontSize: isMobile ? '0.75rem' : '16px',
              fontWeight: 'bold',
              minWidth: isMobile ? '80px' : '120px',
              px: isMobile ? 0.5 : 2,
            },
            '& .Mui-selected': {
              color: colors.blueAccent[500],
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.blueAccent[500],
            },
          }}
        >
          <Tab label={t('overviewTab', 'Overview')} />
          <Tab label={t('gradientAnalysisTab', 'Gradient')} />
          <Tab label={t('speedAnalysisTab', 'Speed')} />
          <Tab label={t('climbsTab', 'Climbs')} />
          <Tab label={t('descentsTab', 'Descents')} />
          {(workoutData?.climbs?.length > 0 || workoutData?.descents?.length > 0) && (
            <Tab label="Plan" />
          )}
        </Tabs>
      </Paper>

      {/* Overview: map + all charts */}
      {analysisType === 0 && (
        <Box>
          {chunks.some(c => c.points && c.points.length > 0) && (
            <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 3, mb: 3 }}>
              <BikeRouteMap chunks={chunks} title={t('routeMap', 'Route Map')} />
            </Paper>
          )}
          <WorkoutChartsSection chunks={chunks} workoutData={workoutData} />
          <StaminaChart workoutData={workoutData} />
        </Box>
      )}

      {analysisType === 1 && (
        <GradientAnalysisSection chunks={chunks} workoutData={workoutData} />
      )}

      {analysisType === 2 && (
        <SpeedAnalysisSection chunks={chunks} workoutData={workoutData} />
      )}

      {analysisType === 3 && (
        <Box>
          {chunks.some(c => c.points && c.points.length > 0) && (
            <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 3, mb: 3 }}>
              <BikeRouteMap
                chunks={chunks}
                title={t('climbMapTitle', 'Climb Map')}
                climbHighlights={workoutData?.climbs || []}
              />
            </Paper>
          )}
          <BikeClimbsChart
            chunks={chunks}
            climbs={workoutData?.climbs || []}
            activeClimbRange={activeClimbRange}
          />
          <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 3, mb: 3, mt: 3 }}>
            {workoutData?.climbs?.length > 0 ? (
              <BikeClimbsGrid
                climbs={workoutData.climbs}
                onClimbClick={(range) => setActiveClimbRange(range)}
                onClimbDoubleClick={(range) => setActiveClimbRange(range)}
              />
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color={colors.grey[400]}>No significant climbs detected in this activity.</Typography>
                <Typography variant="caption" color={colors.grey[600]} mt={1} display="block">
                  Climbs require at least 50 m gain, 300 m length, and 0.5% average gradient.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {analysisType === 4 && (
        <Box>
          {chunks.some(c => c.points && c.points.length > 0) && (
            <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 3, mb: 3 }}>
              <BikeRouteMap
                chunks={chunks}
                title={t('descentMapTitle', 'Descent Map')}
                descentHighlights={workoutData?.descents || []}
              />
            </Paper>
          )}
          <BikeClimbsChart
            chunks={chunks}
            descents={workoutData?.descents || []}
            activeDescentRange={activeDescentRange}
          />
          <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 3, mb: 3, mt: 3 }}>
            {workoutData?.descents?.length > 0 ? (
              <BikeDescentGrid
                descents={workoutData.descents}
                onDescentClick={(range) => setActiveDescentRange(range)}
                onDescentDoubleClick={(range) => setActiveDescentRange(range)}
              />
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color={colors.grey[400]}>No significant descents detected in this activity.</Typography>
                <Typography variant="caption" color={colors.grey[600]} mt={1} display="block">
                  Descents require at least 50 m loss, 300 m length, and 0.5% average gradient.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Tab 5: Plan */}
      {analysisType === 5 && (
        <Box>
          {!bikePlanSegments && !bikePlanLoading && (
            <Box textAlign="center" py={4}>
              <Typography color={colors.grey[300]} mb={2}>
                Generate a segment-by-segment bike plan with power targets and zone recommendations.
              </Typography>
              <Button
                variant="contained"
                onClick={async () => {
                  const sessionId = workoutData?.analysis_session_id;
                  if (!sessionId) return;
                  setBikePlanLoading(true);
                  setBikePlanError('');
                  try {
                    const data = await buildBikePlan(sessionId);
                    setBikePlanSegments(data.bike_plan_segments || []);
                  } catch (e) {
                    setBikePlanError(e?.response?.data?.detail || 'Failed to build bike plan');
                  } finally {
                    setBikePlanLoading(false);
                  }
                }}
                sx={{ backgroundColor: colors.blueAccent[600], '&:hover': { backgroundColor: colors.blueAccent[500] } }}
              >
                Build Bike Plan
              </Button>
              {bikePlanError && <Alert severity="error" sx={{ mt: 2 }}>{bikePlanError}</Alert>}
            </Box>
          )}

          {bikePlanLoading && (
            <Box textAlign="center" py={6}>
              <CircularProgress sx={{ color: colors.blueAccent[400] }} />
              <Typography color={colors.grey[300]} mt={2}>Building bike plan…</Typography>
            </Box>
          )}

          {bikePlanSegments && bikePlanSegments.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography color={colors.grey[400]}>No segments generated — the route may lack significant climbs or descents.</Typography>
            </Box>
          )}

          {bikePlanSegments && bikePlanSegments.length > 0 && (() => {
            const sessionId = workoutData?.analysis_session_id;
            const routeName = workoutData?.race?.name || workoutData?.fit_filename || 'Bike Plan';

            const handleDownloadFit = async () => {
              try {
                const resp = await downloadBikePlanFit(sessionId);
                const url = window.URL.createObjectURL(new Blob([resp.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `mkpace_bike_plan.fit`;
                a.click();
                window.URL.revokeObjectURL(url);
              } catch (e) { console.error(e); }
            };

            const handleDownloadZwo = async () => {
              try {
                const resp = await downloadBikePlanZwo(sessionId);
                const url = window.URL.createObjectURL(new Blob([resp.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `mkpace_bike_plan.zwo`;
                a.click();
                window.URL.revokeObjectURL(url);
              } catch (e) { console.error(e); }
            };

            return (
              <>
                {/* Map */}
                {chunks.some(c => c.points && c.points.length > 0) && (
                  <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 3, mb: 3 }}>
                    <BikeRouteMap
                      chunks={chunks}
                      title="Plan Map"
                      segmentHighlight={planSelectedSegment}
                    />
                  </Paper>
                )}

                {/* Elevation + metrics chart with plan boundary lines */}
                <BikeClimbsChart
                  chunks={chunks}
                  planSegments={bikePlanSegments}
                  activePlanSegment={planSelectedSegment}
                  externalZoom={planChartZoom}
                />

                {/* Download / print actions */}
                <Box display="flex" justifyContent="flex-end" gap={1} mt={2} mb={2} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintBikePlan}
                    sx={{ color: colors.grey[200], borderColor: colors.grey[600] }}
                  >
                    Print
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadFit}
                    sx={{ color: colors.grey[200], borderColor: colors.grey[600] }}
                  >
                    Garmin .fit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadZwo}
                    sx={{ color: colors.grey[200], borderColor: colors.grey[600] }}
                  >
                    Zwift .zwo
                  </Button>
                </Box>

                {/* Plan grid — single-click highlights map+chart, double-click zooms chart */}
                <Paper elevation={3} sx={{ backgroundColor: colors.grey[800], borderRadius: '8px', p: 2, mb: 3 }}>
                  <BikePlanGrid
                    segments={bikePlanSegments}
                    onSegmentSelect={(seg) => {
                      setPlanSelectedSegment(seg);
                      if (seg) setPlanChartZoom(null);
                    }}
                    onSegmentDblClick={(seg) => {
                      setPlanChartZoom({
                        leftKm: seg.start_distance_m / 1000,
                        rightKm: seg.end_distance_m / 1000,
                      });
                    }}
                  />
                </Paper>

                <Box sx={{ display: 'none' }}>
                  <BikePlanPrintContent
                    ref={bikePlanPrintRef}
                    chunks={chunks}
                    bikePlanSegments={bikePlanSegments}
                    routeName={routeName}
                  />
                </Box>
              </>
            );
          })()}
        </Box>
      )}

      {/* Bottom Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={4} mb={2} flexWrap="wrap">
        <Button
          variant="outlined"
          startIcon={!isMobile && <MoreHorizIcon />}
          onClick={() => setShowMoreActionsPanel(true)}
          sx={{
            color: colors.grey[100],
            borderColor: colors.primary[400],
            minWidth: isMobile ? 'auto' : undefined,
            px: isMobile ? 1 : undefined,
            '&:hover': {
              borderColor: colors.primary[300],
              backgroundColor: colors.primary[700]
            }
          }}
        >
          {isMobile ? <MoreHorizIcon /> : t('moreActions', "Let's train")}
        </Button>
        {!shareId && workoutData?.analysis_session_id && !!user && (
          <Button
            variant="outlined"
            startIcon={!isMobile && <ShareIcon />}
            onClick={() => setShowShareDialog(true)}
            sx={{
              color: colors.grey[100],
              borderColor: colors.primary[400],
              minWidth: isMobile ? 'auto' : undefined,
              px: isMobile ? 1 : undefined,
              '&:hover': {
                borderColor: colors.primary[300],
                backgroundColor: colors.primary[700]
              }
            }}
          >
            {isMobile ? <ShareIcon /> : t('share', 'Share')}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => navigate('/bike')}
          sx={{
            color: colors.grey[100],
            borderColor: colors.primary[400],
            minWidth: isMobile ? 'auto' : undefined,
            px: isMobile ? 2 : undefined,
            fontSize: isMobile ? '0.75rem' : undefined,
            '&:hover': {
              borderColor: colors.primary[300],
              backgroundColor: colors.primary[700]
            }
          }}
        >
          {isMobile ? t('dashboardTitle', 'Biking Analysis') : t('backToDashboard', 'Back to Dashboard')}
        </Button>
      </Box>
      </Box>

      <ShareBikeAnalysisDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        analysisSessionId={workoutData?.analysis_session_id}
      />

      {/* Unshare confirmation dialog (share mode only) */}
      <Dialog open={unshareDialogOpen} onClose={() => setUnshareDialogOpen(false)}>
        <DialogTitle>Unshare Analysis?</DialogTitle>
        <DialogContent>
          <Typography>This will remove the public link. Anyone with the link will no longer be able to access it.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnshareDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUnshare}
            disabled={unshareLoading}
            startIcon={unshareLoading && <CircularProgress size={16} />}
          >
            {unshareLoading ? 'Removing...' : 'Unshare'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* More Actions Panel */}
      <MoreActionsPanel
        open={showMoreActionsPanel}
        onClose={() => setShowMoreActionsPanel(false)}
        workoutData={workoutData}
        gpxFile={gpxFile}
        storedFile={storedFile}
      />

      <GpxPickerDrawer
        open={gpxPickerOpen}
        onClose={() => setGpxPickerOpen(false)}
        onSelect={handleGpxPickerSelect}
        units={units}
      />

      <BikeComparisonSelectorDrawer
        open={compareDrawerOpen}
        onClose={() => setCompareDrawerOpen(false)}
        onStart={(source) => {
          setCompareDrawerOpen(false);
          navigate('/compare', {
            state: {
              baseSessionId: workoutData?.analysis_session_id,
              source,
              aFilename: workoutData?.fit_filename || storedFile?.name || 'Workout A',
            },
          });
        }}
      />
    </Box>
  );
};

export default WorkoutAnalysisV2;
